import { Card, CardContent } from "@/components/ui/card";
import { tr } from "@/utils/i18n";
import { Button } from "@/components/ui/button";
import { Zap, Sparkles } from "lucide-react";

const L = (language, pt, es, en) =>
  language === 'pt' ? pt : language === 'es' ? es : en;

function PlanFeatures({ items }) {
  return (
    <ul className="space-y-3 mb-6">
      {items.map((item) => (
        <li key={item.text} className={`flex items-center gap-2 text-sm ${item.highlight ? 'text-green-400' : 'text-zinc-300'}`}>
          {item.highlight ? <Sparkles className="w-4 h-4" /> : <Zap className="w-4 h-4 text-cyan-400" />}
          {item.text}
        </li>
      ))}
    </ul>
  );
}

export function PricingSection({ language, currencySymbol, navigate }) {
  const freeFeatures = [
    { text: L(language, '1 ciclo por mês', '1 ciclo por mes', '1 cycle per month') },
    { text: L(language, 'Guia para iniciantes', 'Guía para principiantes', 'Beginner guide') },
    { text: L(language, 'Exportar PDF', 'Exportar PDF', 'PDF export') },
  ];
  const monthlyFeatures = [
    { text: L(language, 'Ciclos ilimitados', 'Ciclos ilimitados', 'Unlimited cycles') },
    { text: L(language, '30% OFF na Summit Peptides', '30% OFF en Summit Peptides', '30% OFF at Summit Peptides') },
    { text: L(language, 'Compartilhar ciclos', 'Compartir ciclos', 'Share cycles') },
    { text: L(language, 'Peptídeos personalizados', 'Péptidos personalizados', 'Custom peptides') },
    { text: L(language, 'Colaboradores', 'Colaboradores', 'Collaborators') },
    { text: L(language, '30 dias GRÁTIS com LAUNCH30!', '¡30 días GRATIS con LAUNCH30!', '30 days FREE with LAUNCH30!'), highlight: true },
  ];
  const yearlyFeatures = [
    { text: L(language, 'Tudo do Mensal', 'Todo del Mensual', 'Everything in Monthly') },
    { text: L(language, '2 meses grátis', '2 meses gratis', '2 months free') },
    { text: L(language, 'Suporte prioritário', 'Soporte prioritario', 'Priority support') },
    { text: L(language, '30 dias GRÁTIS com LAUNCH30!', '¡30 días GRATIS con LAUNCH30!', '30 days FREE with LAUNCH30!'), highlight: true },
  ];

  const trialCta = L(language, 'Começar 30 Dias Grátis', 'Iniciar 30 Días Gratis', 'Start 30-Day Free Trial');

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8" id="pricing" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-white">
            {L(language, 'Planos Simples e Transparentes', 'Precios Simples y Transparentes', 'Simple, Transparent Pricing')}
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            {L(language, 'Comece grátis ou desbloqueie todo o potencial com Premium', 'Empieza gratis o desbloquea todo el potencial con Premium', 'Start free or unlock full potential with Premium')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-zinc-950/50 border-zinc-800" data-testid="pricing-free">
            <CardContent className="p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{L(language, 'Grátis', 'Gratis', 'Free')}</h3>
                <p className="text-zinc-400 text-sm">{L(language, 'Perfeito para começar', 'Perfecto para empezar', 'Perfect to get started')}</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$0</span>
                <span className="text-zinc-500">/mo</span>
              </div>
              <PlanFeatures items={freeFeatures} />
              <Button variant="outline" className="w-full" disabled>
                {L(language, 'Plano Atual', 'Plan Actual', 'Current Plan')}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-950/30 to-zinc-950 border-cyan-500/30 relative" data-testid="pricing-monthly">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                {L(language, 'MAIS POPULAR', 'MÁS POPULAR', 'MOST POPULAR')}
              </span>
            </div>
            <CardContent className="p-6 pt-8">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{L(language, 'Premium Mensal', 'Premium Mensual', 'Premium Monthly')}</h3>
                <p className="text-zinc-400 text-sm">{L(language, 'Para entusiastas sérios', 'Para entusiastas serios', 'For serious enthusiasts')}</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">{currencySymbol}9.99</span>
                <span className="text-zinc-500">/mo</span>
              </div>
              <PlanFeatures items={monthlyFeatures} />
              <Button
                className="w-full btn-primary"
                onClick={() => navigate('/subscription?plan=monthly')}
                data-testid="pricing-monthly-cta"
              >
                {trialCta}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-zinc-950/50 border-zinc-800 relative" data-testid="pricing-yearly">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-green-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                {L(language, 'ECONOMIZE 17%', 'AHORRA 17%', 'SAVE 17%')}
              </span>
            </div>
            <CardContent className="p-6 pt-8">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{L(language, 'Premium Anual', 'Premium Anual', 'Premium Yearly')}</h3>
                <p className="text-zinc-400 text-sm">{L(language, 'Melhor valor', 'Mejor valor', 'Best value')}</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">{currencySymbol}99.99</span>
                <span className="text-zinc-500">/yr</span>
              </div>
              <PlanFeatures items={yearlyFeatures} />
              <Button
                variant="outline"
                className="w-full border-cyan-500/50 hover:bg-cyan-500/10"
                onClick={() => navigate('/subscription?plan=yearly')}
                data-testid="pricing-yearly-cta"
              >
                {trialCta}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
